import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { USE_MOCK, surveyApi, analyticsApi, ApiError } from '@/api/client';
import { MOCK_DASHBOARD } from '@/data/mockSurveys';
import type { SurveyInstance, HomeQuestion, HomeQuestionType, HomeEnfoque, HomePresentMode } from '@/types/survey.types';
import type { SurveyInstanceApiDto, SurveyQuestionApiDto, MyStatsResponse } from '@/api/types';
import { useUserContext } from '@/contexts/UserContext';

function adaptQuestion(q: SurveyQuestionApiDto): HomeQuestion {
  const lang = i18next.language ?? 'es';
  const type: HomeQuestionType =
    q.questionType === 'nps' ? 'nps'
    : q.questionType === 'open' ? 'open'
    : 'likert';
  return {
    id: q.id,
    type,
    rawType: q.questionType,  // preserve original for submission
    enfoque: (q.focusType as HomeEnfoque) ?? 'individual',
    presentMode: (q.presentationMode as HomePresentMode) ?? 'matrix',
    text: q.text[lang] ?? q.text['es'] ?? Object.values(q.text)[0] ?? '',
    required: true,
    fundamentalId: q.fundamentalId ?? undefined,
  };
}

function adaptInstance(pi: SurveyInstanceApiDto): SurveyInstance {
  return {
    id: pi.id,
    meetingTitle: pi.meetingSubject,
    meetingDate: pi.createdAt,
    durationMinutes: 0,
    participants: (pi.participants ?? []).map(p => p.displayName),
    expiresAt: pi.expiresAt,
    priority: 0,
    questions: pi.questions.map(adaptQuestion),
  };
}

/** Intervalo de sondeo de encuestas pendientes. Una encuesta aparece minutos después de que
 *  termina la reunión, sin que el usuario haga nada en la app, así que la vista tiene que
 *  buscarla sola: con el staleTime global de 5 min heredado de main.tsx, la única forma de
 *  verla era recargar la página completa. */
const PENDING_POLL_INTERVAL_MS = 60_000;

/**
 * El backend responde 422 cuando la instancia ya no admite acción: venció su ventana de
 * vigencia, ya fue respondida o no pertenece al usuario. En los tres casos reintentar no
 * sirve — hay que refrescar la lista y avisar que la encuesta dejó de estar disponible.
 */
export function isSurveyUnavailableError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 422;
}

export function usePendingInstances() {
  const { demoUser } = useUserContext();
  const activeUserId = demoUser.userId;

  return useQuery<SurveyInstance[]>({
    queryKey: ['pendingInstances', activeUserId],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_DASHBOARD.instances;
      const data = await surveyApi.getPendingInstances(activeUserId);
      return data.items.map(adaptInstance);
    },
    staleTime: 0,
    refetchInterval: PENDING_POLL_INTERVAL_MS,
    // El caso típico es volver a la pestaña de Teams tras la reunión: ahí debe refrescar ya,
    // sin esperar al siguiente tick del intervalo.
    refetchOnWindowFocus: 'always',
  });
}

/**
 * Refresca lo que depende del estado de las instancias: la lista de pendientes y los
 * contadores del dashboard. Se centraliza aquí porque la disparan tanto el éxito de una
 * mutación como el 422 que indica que la encuesta dejó de estar disponible.
 */
function invalidateInstanceQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['pendingInstances'] }).catch((err: unknown) => {
    if (import.meta.env.DEV) console.error('[pendingInstances] invalidate failed', err);
  });
  queryClient.invalidateQueries({ queryKey: ['dashboardStats'] }).catch((err: unknown) => {
    if (import.meta.env.DEV) console.error('[dashboardStats] invalidate failed', err);
  });
}

export function useSubmitResponse() {
  const qc = useQueryClient();
  const { demoUser } = useUserContext();
  const activeUserId = demoUser.userId;
  return useMutation({
    mutationFn: ({
      instanceId,
      answers,
      questions = [],
      skipped,
    }: {
      instanceId: string;
      answers: Record<string, string | number>;
      questions?: import('@/types/survey.types').HomeQuestion[];
      skipped?: boolean;
    }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] submitResponse', { instanceId, answers, skipped });
        return Promise.resolve({ success: true, instanceId });
      }
      // Transform flat dict {questionId_pN / questionId__pN: value} → array [{questionId, questionType, score, openText}]
      // Two suffix formats exist:
      //   individual+matrix  → questionId__pN  (double underscore, isMatrixQuestion=true)
      //   individual+individual → questionId_pN (single underscore, expanded per participant)
      // Strip the suffix with double-underscore first to avoid stripping only "_pN" from "__pN".
      const answersArray = Object.entries(answers).map(([key, value]) => {
        const baseId = key.replace(/__p\d+$/, '').replace(/_p\d+$/, '');
        const q = questions.find(q => q.id === baseId);
        return {
          questionId: key,
          questionType: q?.rawType ?? 'behavior',
          score: typeof value === 'number' ? value : null,
          openText: typeof value === 'string' ? value : null,
        };
      });
      return surveyApi.submitResponse(instanceId, answersArray, activeUserId);
    },
    onSuccess: () => invalidateInstanceQueries(qc),
    onError: (err: unknown) => {
      // La encuesta dejó de ser respondible: se recarga la lista para que desaparezca.
      if (isSurveyUnavailableError(err)) invalidateInstanceQueries(qc);
    },
  });
}

export function useOmitInstance() {
  const qc = useQueryClient();
  const { demoUser } = useUserContext();
  const activeUserId = demoUser.userId;
  return useMutation({
    mutationFn: (instanceId: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] omitInstance', instanceId);
        return Promise.resolve({ success: true });
      }
      return surveyApi.omitInstance(instanceId, activeUserId);
    },
    onSuccess: () => invalidateInstanceQueries(qc),
    onError: (err: unknown) => {
      if (isSurveyUnavailableError(err)) invalidateInstanceQueries(qc);
    },
  });
}

export function useDashboardStats() {
  const { demoUser } = useUserContext();
  const activeUserId = demoUser.userId;
  return useQuery<MyStatsResponse>({
    queryKey: ['dashboardStats', activeUserId],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_DASHBOARD.stats as unknown as MyStatsResponse;
      return surveyApi.getMyStats(activeUserId);
    },
    // Los contadores cambian con la misma llegada de encuestas: se refrescan a la par.
    staleTime: 0,
    refetchInterval: PENDING_POLL_INTERVAL_MS,
    refetchOnWindowFocus: 'always',
  });
}

export function useCurrentScore() {
  const { demoUser } = useUserContext();
  const activeUserId = demoUser.userId;
  return useQuery({
    queryKey: ['currentScore', activeUserId],
    retry: false,
    queryFn: async () => {
      if (USE_MOCK) return MOCK_DASHBOARD.currentScore;
      try {
        const personal = await analyticsApi.getPersonal(activeUserId);
        return personal.avgScore;
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 404) return 0;
        if (err instanceof TypeError) return 0;
        throw err;
      }
    },
  });
}
